import React, { useMemo, useState } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import TabsBase from "../../components/reusable/Navigation/TabsBase";
import ContainerBase from "../../components/framepart/SubpageLayout/ContainerBase";
import ToolbarHeadSubpage from "../../components/framepart/SubpageLayout/ToolbarHeadSubpage";
import DropdownMulti from "../../components/reusable/Forms/DropdownMulti";
import Button from "../../components/reusable/Forms/Button";
import TextFilter from "../../components/reusable/Forms/TextFilter";
import Switch from "../../components/reusable/Forms/Switch";

import DrawSpace25D from "../../components/spatial/DrawSpace25D";
import DrawPropertiesPanel from "../../components/spatial/components/DrawPropertiesPanel";

import ProjectScopeReport from "../../components/defined/reporting/ProjectScopeReport";
import PrintMenuStandard from "../../components/defined/Print/PrintMenuStandard";

/* ===== Journey Navigation ===== */

import JourneyTabs from "../../components/reusable/Navigation/JourneyTabs";
/* ============================================================================
   WORKBENCH GÉNÉRIQUE
============================================================================ */
import AssetWorkbenchWorkspace from "../../components/defined/TechnicalSystem/AssetWorkbenchWorkspace";
import AssetWorkbenchDetailsPanel from "../../components/defined/TechnicalSystem/AssetWorkbenchDetailsPanel";

/* ============================================================================
   SYMBOLS
============================================================================ */
import TechnicalSystemsSymbol from "../../components/defined/TechnicalSystem/symbols/TechnicalSystemsSymbol";
import SpecificAssetsSymbol from "../../components/defined/TechnicalSystem/symbols/SpecificAssetsSymbol";
import BuildingSystemsSymbol from "../../components/defined/TechnicalSystem/symbols/BuildingSystemsSymbol";

/* ============================================================================
   DEFINITIONS MÉTIER
============================================================================ */
import {
  familyMeta as technicalSystemsFamilyMeta,
  fieldSchema as technicalSystemsFieldSchema,
  createDefaultTechnicalSystems,
} from "../../components/defined/TechnicalSystem/definitions/technicalSystems.definition";

import {
  familyMeta as specificAssetsFamilyMeta,
  fieldSchema as specificAssetsFieldSchema,
  createDefaultSpecificAssets,
} from "../../components/defined/TechnicalSystem/definitions/specificAssets.definition";

import {
  familyMeta as buildingSystemsFamilyMeta,
  fieldSchema as buildingSystemsFieldSchema,
  createDefaultBuildingSystems,
} from "../../components/defined/TechnicalSystem/definitions/buildingSystems.definition";

/* ============================================================================
   HELPERS WORKBENCH
============================================================================ */
import {
  duplicateWorkbenchItem,
  deleteWorkbenchItem,
  updateWorkbenchItem,
} from "../../components/defined/TechnicalSystem/AssetWorkbench";

/* ============================================================
   OPTIONS GÉNÉRIQUES
============================================================ */
const demoOptions = [
  { value: "opt1", label: "Option 1" },
  { value: "opt2", label: "Option 2" },
  { value: "opt3", label: "Option 3" },
];

const projectIdentity = {
  projectName: "Demo Project",
  reference: "PRJ-001",
  clientName: "Client Demo",
  location: "Project Location",
  projectType: "Infrastructure / Building",
  country: "—",
};

/* ============================================================
   STYLES COMMUNS
============================================================ */
const baseLeftZoneStyle = {
  flex: 3,
  minWidth: 700,
  minHeight: "calc(100vh - 290px)",
  padding: 0,
  borderRadius: 6,
  // background: theme.colors.background.surface,
  overflowX: "auto",
  overflowY: "auto",
  flexShrink: 0,
};

const baseRightZoneStyle = {
  flex: 1,
  minWidth: 750,
  minHeight: "calc(100vh - 290px)",
  padding: 12,
  borderRadius: 6,
  background: theme.colors.background.surface,
  border: `1px solid ${theme.colors.borders.default}`,
  overflowX: "auto",
  overflowY: "auto",
  boxSizing: "border-box",
  flexShrink: 0,
  transition: "flex 0.16s ease, min-width 0.16s ease, max-width 0.16s ease",
};

/* ============================================================
   PAGE
============================================================ */
const Subpage_102_architecture = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [switchOn, setSwitchOn] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [buildingSpatialOpen, setBuildingSpatialOpen] = useState(false);
  const [technicalSpatialOpen, setTechnicalSpatialOpen] = useState(false);
  const [specificSpatialOpen, setSpecificSpatialOpen] = useState(false);

  /* ============================================================
     SPATIAL
  ============================================================ */
  const [selectedSpatialEntity, setSelectedSpatialEntity] = useState(null);
  const [editedSpatialEntity, setEditedSpatialEntity] = useState(null);

  const [spatialEntities] = useState([
    {
      id: "site-main",
      spatialType: "SITE",
      name: "Main Site",
      elevation: 0,
      area: 12000,
      perimeter: 460,
    },
    {
      id: "level-rdc",
      spatialType: "LEVEL",
      name: "Ground Floor",
      elevation: 0,
      area: 2400,
      perimeter: 210,
    },
    {
      id: "level-r1",
      spatialType: "LEVEL",
      name: "Level 1",
      elevation: 4,
      area: 2200,
      perimeter: 205,
    },
    {
      id: "zone-a",
      spatialType: "ZONE",
      name: "Zone A",
      elevation: 0,
      area: 450,
      perimeter: 98,
    },
    {
      id: "zone-b",
      spatialType: "ZONE",
      name: "Zone B",
      elevation: 0,
      area: 620,
      perimeter: 114,
    },
  ]);

  /* ============================================================
     TECHNICAL SYSTEMS
  ============================================================ */
  const [technicalSystems, setTechnicalSystems] = useState(() =>
    createDefaultTechnicalSystems()
  );

  const [selectedTechnicalSystemId, setSelectedTechnicalSystemId] = useState(() => {
    const initialItems = createDefaultTechnicalSystems();
    return initialItems[0]?.id ?? null;
  });

  const filteredTechnicalSystems = useMemo(() => {
    let next = [...technicalSystems];

    if (switchOn) {
      next = next.filter((item) => item.active);
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (normalizedSearch) {
      next = next.filter((item) => {
        const title = item.title?.toLowerCase() ?? "";
        const subtitle = item.subtitle?.toLowerCase() ?? "";
        const family = item.family?.toLowerCase() ?? "";

        return (
          title.includes(normalizedSearch) ||
          subtitle.includes(normalizedSearch) ||
          family.includes(normalizedSearch)
        );
      });
    }

    return next;
  }, [technicalSystems, switchOn, searchTerm]);

  const effectiveSelectedTechnicalSystemId = useMemo(() => {
    const existsInFiltered = filteredTechnicalSystems.some(
      (item) => item.id === selectedTechnicalSystemId
    );

    if (existsInFiltered) return selectedTechnicalSystemId;
    return filteredTechnicalSystems[0]?.id ?? null;
  }, [filteredTechnicalSystems, selectedTechnicalSystemId]);

  const handleDuplicateTechnicalSystem = (itemId) => {
    const { nextItems, duplicatedItem } = duplicateWorkbenchItem(
      technicalSystems,
      itemId
    );

    setTechnicalSystems(nextItems);

    if (duplicatedItem) {
      setSelectedTechnicalSystemId(duplicatedItem.id);
    }
  };

  const handleDeleteTechnicalSystem = (itemId) => {
    const { nextItems, nextSelectedItemId } = deleteWorkbenchItem(
      technicalSystems,
      itemId,
      selectedTechnicalSystemId
    );

    setTechnicalSystems(nextItems);
    setSelectedTechnicalSystemId(nextSelectedItemId);
  };

  const handleAddSpatialAssignmentToTechnicalSystem = (itemId, spatialEntityId) => {
    const spatialEntity = spatialEntities.find((entity) => entity.id === spatialEntityId);
    if (!spatialEntity) return;

    const nextItems = updateWorkbenchItem(technicalSystems, itemId, (item) => {
      const currentAssignments = Array.isArray(item.spatialAssignments)
        ? item.spatialAssignments
        : [];

      const alreadyExists = currentAssignments.some(
        (assignment) => assignment.spatialEntityId === spatialEntityId
      );

      if (alreadyExists) return item;

      return {
        ...item,
        spatialAssignments: [
          ...currentAssignments,
          {
            spatialEntityId,
            spatialType: spatialEntity.spatialType,
            allocationRole: "PRIMARY",
          },
        ],
      };
    });

    setTechnicalSystems(nextItems);
  };

  const handleRemoveSpatialAssignmentFromTechnicalSystem = (itemId, spatialEntityId) => {
    const nextItems = updateWorkbenchItem(technicalSystems, itemId, (item) => ({
      ...item,
      spatialAssignments: (item.spatialAssignments || []).filter(
        (assignment) => assignment.spatialEntityId !== spatialEntityId
      ),
    }));

    setTechnicalSystems(nextItems);
  };

  /* ============================================================
     SPECIFIC ASSETS
  ============================================================ */
  const [specificAssets, setSpecificAssets] = useState(() =>
    createDefaultSpecificAssets()
  );

  const [selectedSpecificAssetId, setSelectedSpecificAssetId] = useState(() => {
    const initialItems = createDefaultSpecificAssets();
    return initialItems[0]?.id ?? null;
  });

  const handleDuplicateSpecificAsset = (itemId) => {
    const { nextItems, duplicatedItem } = duplicateWorkbenchItem(
      specificAssets,
      itemId
    );

    setSpecificAssets(nextItems);

    if (duplicatedItem) {
      setSelectedSpecificAssetId(duplicatedItem.id);
    }
  };

  const handleDeleteSpecificAsset = (itemId) => {
    const { nextItems, nextSelectedItemId } = deleteWorkbenchItem(
      specificAssets,
      itemId,
      selectedSpecificAssetId
    );

    setSpecificAssets(nextItems);
    setSelectedSpecificAssetId(nextSelectedItemId);
  };

  const handleAddSpatialAssignmentToSpecificAsset = (itemId, spatialEntityId) => {
    const spatialEntity = spatialEntities.find((entity) => entity.id === spatialEntityId);
    if (!spatialEntity) return;

    const nextItems = updateWorkbenchItem(specificAssets, itemId, (item) => {
      const currentAssignments = Array.isArray(item.spatialAssignments)
        ? item.spatialAssignments
        : [];

      const alreadyExists = currentAssignments.some(
        (assignment) => assignment.spatialEntityId === spatialEntityId
      );

      if (alreadyExists) return item;

      return {
        ...item,
        spatialAssignments: [
          ...currentAssignments,
          {
            spatialEntityId,
            spatialType: spatialEntity.spatialType,
            allocationRole: "PRIMARY",
          },
        ],
      };
    });

    setSpecificAssets(nextItems);
  };

  const handleRemoveSpatialAssignmentFromSpecificAsset = (itemId, spatialEntityId) => {
    const nextItems = updateWorkbenchItem(specificAssets, itemId, (item) => ({
      ...item,
      spatialAssignments: (item.spatialAssignments || []).filter(
        (assignment) => assignment.spatialEntityId !== spatialEntityId
      ),
    }));

    setSpecificAssets(nextItems);
  };

  /* ============================================================
     BUILDING SYSTEMS
  ============================================================ */
  const [buildingSystems, setBuildingSystems] = useState(() =>
    createDefaultBuildingSystems()
  );

  const [selectedBuildingSystemId, setSelectedBuildingSystemId] = useState(() => {
    const initialItems = createDefaultBuildingSystems();
    return initialItems[0]?.id ?? null;
  });

  const handleDuplicateBuildingSystem = (itemId) => {
    const { nextItems, duplicatedItem } = duplicateWorkbenchItem(
      buildingSystems,
      itemId
    );

    setBuildingSystems(nextItems);

    if (duplicatedItem) {
      setSelectedBuildingSystemId(duplicatedItem.id);
    }
  };

  const handleDeleteBuildingSystem = (itemId) => {
    const { nextItems, nextSelectedItemId } = deleteWorkbenchItem(
      buildingSystems,
      itemId,
      selectedBuildingSystemId
    );

    setBuildingSystems(nextItems);
    setSelectedBuildingSystemId(nextSelectedItemId);
  };

  const handleAddSpatialAssignmentToBuildingSystem = (itemId, spatialEntityId) => {
    const spatialEntity = spatialEntities.find((entity) => entity.id === spatialEntityId);
    if (!spatialEntity) return;

    const nextItems = updateWorkbenchItem(buildingSystems, itemId, (item) => {
      const currentAssignments = Array.isArray(item.spatialAssignments)
        ? item.spatialAssignments
        : [];

      const alreadyExists = currentAssignments.some(
        (assignment) => assignment.spatialEntityId === spatialEntityId
      );

      if (alreadyExists) return item;

      return {
        ...item,
        spatialAssignments: [
          ...currentAssignments,
          {
            spatialEntityId,
            spatialType: spatialEntity.spatialType,
            allocationRole: "PRIMARY",
          },
        ],
      };
    });

    setBuildingSystems(nextItems);
  };

  const handleRemoveSpatialAssignmentFromBuildingSystem = (itemId, spatialEntityId) => {
    const nextItems = updateWorkbenchItem(buildingSystems, itemId, (item) => ({
      ...item,
      spatialAssignments: (item.spatialAssignments || []).filter(
        (assignment) => assignment.spatialEntityId !== spatialEntityId
      ),
    }));

    setBuildingSystems(nextItems);
  };

 
  /* ============================================================
     ZONE A1
  ============================================================ */
  const zoneA1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 6,
    width: 250,
    sections: [
      {
        title: "",
        content: (
          <DrawSpace25D
            projectId="demo-project"
            assetId="demo-asset"
            width="100%"
            height={680}
            assetLabel="Asset"
            showToolbar={true}
            showFloorBar={false}
            showPropertiesPanel={false}
            showGrid={true}
            enablePersistence={false}
            enablePrint={true}
            readOnlyMode={false}
            disabled={false}
            printTitle="Spatial Plan"
            printOrientation="landscape"
            editedEntity={editedSpatialEntity}
            onChange={(data) => console.log("DrawSpace changed:", data)}
            onSelectionChange={(entity) => {
              setSelectedSpatialEntity(entity);
              setEditedSpatialEntity(entity);
            }}
            onDataLoaded={(data) => console.log("DrawSpace loaded:", data)}
          />
        ),
      },
    ],
  };

  /* ============================================================
     ZONE A2
  ============================================================ */
  const zoneA2 = {
    style: {
      ...baseRightZoneStyle,
      flex: "0 0 360px",
      minWidth: 360,
      maxWidth: 360,
    },
    gap: 12,
    sectionGap: 6,
    width: 250,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Properties",
        content: (
          <DrawPropertiesPanel
            entity={editedSpatialEntity}
            onChange={setEditedSpatialEntity}
          />
        ),
      },
    ],
  };
 /* ============================================================
   ZONE B1
============================================================ */
const zoneB1 = {
  style: baseLeftZoneStyle,
  gap: 16,
  sectionGap: 6,
  sections: [
    {
      title: "",
      content: (
        <AssetWorkbenchWorkspace
          title=""
          subtitle=""
          items={buildingSystems}
          selectedItemId={selectedBuildingSystemId}
          onSelectItem={setSelectedBuildingSystemId}
          onDuplicateItem={handleDuplicateBuildingSystem}
          familyMeta={buildingSystemsFamilyMeta}
          SymbolComponent={BuildingSystemsSymbol}
          infoPanelLabels={{
            total: "Systems",
            active: "Actifs",
            all: "All systems",
          }}
        />
      ),
    },
  ],
};

/* ============================================================
   ZONE B2
============================================================ */
const zoneB2 = {
  style: {
    ...baseRightZoneStyle,
    flex: buildingSpatialOpen ? "0 0 900px" : "0 0 450px",
    minWidth: buildingSpatialOpen ? 900 : 450,
    maxWidth: buildingSpatialOpen ? 900 : 450,
  },
  gap: 12,
  sectionGap: 6,
  sections: [
    {
      icon: "",
      title: "",
      content: (
        <AssetWorkbenchDetailsPanel
          title=""
          subtitle=""
          items={buildingSystems}
          selectedItemId={selectedBuildingSystemId}
          familyMeta={buildingSystemsFamilyMeta}
          fieldSchema={buildingSystemsFieldSchema}
          SymbolComponent={BuildingSystemsSymbol}
          onItemsChange={setBuildingSystems}
          onDuplicateItem={handleDuplicateBuildingSystem}
          onDeleteItem={handleDeleteBuildingSystem}
          emptyMessage="Aucun système bâtiment sélectionné."
          spatialEntities={spatialEntities}
          onAddSpatialAssignment={handleAddSpatialAssignmentToBuildingSystem}
          onRemoveSpatialAssignment={handleRemoveSpatialAssignmentFromBuildingSystem}
          showSpatialAssignment={true}
          spatialPanelOpen={buildingSpatialOpen}
          onSpatialPanelToggle={setBuildingSpatialOpen}
        />
      ),
    },
  ],
};

/* ============================================================
   ZONE C1
============================================================ */
const zoneC1 = {
  style: baseLeftZoneStyle,
  gap: 16,
  sectionGap: 6,
  sections: [
    {
      title: "",
      content: (
        <AssetWorkbenchWorkspace
          title=""
          subtitle=""
          items={filteredTechnicalSystems}
          selectedItemId={effectiveSelectedTechnicalSystemId}
          onSelectItem={setSelectedTechnicalSystemId}
          onDuplicateItem={handleDuplicateTechnicalSystem}
          familyMeta={technicalSystemsFamilyMeta}
          SymbolComponent={TechnicalSystemsSymbol}
          infoPanelLabels={{
            total: "Systèmes",
            active: "Actifs",
            all: "All systems",
          }}
        />
      ),
    },
  ],
};

/* ============================================================
   ZONE C2
============================================================ */
const zoneC2 = {
  style: {
    ...baseRightZoneStyle,
    flex: technicalSpatialOpen ? "0 0 900px" : "0 0 450px",
    minWidth: technicalSpatialOpen ? 900 : 450,
    maxWidth: technicalSpatialOpen ? 900 : 450,
  },
  gap: 12,
  sectionGap: 6,
  sections: [
    {
      icon: "",
      title: "",
      content: (
        <AssetWorkbenchDetailsPanel
          title=""
          subtitle=""
          items={technicalSystems}
          selectedItemId={effectiveSelectedTechnicalSystemId}
          familyMeta={technicalSystemsFamilyMeta}
          fieldSchema={technicalSystemsFieldSchema}
          SymbolComponent={TechnicalSystemsSymbol}
          onItemsChange={setTechnicalSystems}
          onDuplicateItem={handleDuplicateTechnicalSystem}
          onDeleteItem={handleDeleteTechnicalSystem}
          emptyMessage="Aucun système sélectionné."
          spatialEntities={spatialEntities}
          onAddSpatialAssignment={handleAddSpatialAssignmentToTechnicalSystem}
          onRemoveSpatialAssignment={handleRemoveSpatialAssignmentFromTechnicalSystem}
          showSpatialAssignment={true}
          spatialPanelOpen={technicalSpatialOpen}
          onSpatialPanelToggle={setTechnicalSpatialOpen}
        />
      ),
    },
  ],
};

/* ============================================================
   ZONE D1
============================================================ */
const zoneD1 = {
  style: baseLeftZoneStyle,
  gap: 16,
  sectionGap: 6,
  sections: [
    {
      title: "",
      content: (
        <AssetWorkbenchWorkspace
          title=""
          subtitle=""
          items={specificAssets}
          selectedItemId={selectedSpecificAssetId}
          onSelectItem={setSelectedSpecificAssetId}
          onDuplicateItem={handleDuplicateSpecificAsset}
          familyMeta={specificAssetsFamilyMeta}
          SymbolComponent={SpecificAssetsSymbol}
          infoPanelLabels={{
            total: "Assets",
            active: "Actifs",
            all: "All assets",
          }}
        />
      ),
    },
  ],
};

/* ============================================================
   ZONE D2
============================================================ */
const zoneD2 = {
  style: {
    ...baseRightZoneStyle,
    flex: specificSpatialOpen ? "0 0 900px" : "0 0 450px",
    minWidth: specificSpatialOpen ? 900 : 450,
    maxWidth: specificSpatialOpen ? 900 : 450,
  },
  gap: 12,
  sectionGap: 6,
  sections: [
    {
      title: "",
      content: (
        <AssetWorkbenchDetailsPanel
          title=""
          subtitle=""
          items={specificAssets}
          selectedItemId={selectedSpecificAssetId}
          familyMeta={specificAssetsFamilyMeta}
          fieldSchema={specificAssetsFieldSchema}
          SymbolComponent={SpecificAssetsSymbol}
          onItemsChange={setSpecificAssets}
          onDuplicateItem={handleDuplicateSpecificAsset}
          onDeleteItem={handleDeleteSpecificAsset}
          emptyMessage="Aucun ouvrage spécifique sélectionné."
          spatialEntities={spatialEntities}
          onAddSpatialAssignment={handleAddSpatialAssignmentToSpecificAsset}
          onRemoveSpatialAssignment={handleRemoveSpatialAssignmentFromSpecificAsset}
          showSpatialAssignment={true}
          spatialPanelOpen={specificSpatialOpen}
          onSpatialPanelToggle={setSpecificSpatialOpen}
        />
      ),
    },
  ],
};

  /* ============================================================
     ZONE E1
  ============================================================ */
  const zoneE1 = {
    style: baseLeftZoneStyle,
    gap: 12,
    sectionGap: 6,
    width: 250,
    sections: [
      {
        title: "",
        content: (
          <ProjectScopeReport
            projectIdentity={projectIdentity}
            spatialEntities={spatialEntities}
            buildingSystems={buildingSystems}
            technicalSystems={technicalSystems}
            specificAssets={specificAssets}
          />
        ),
      },
    ],
  };

  /* ============================================================
     ZONE E2
  ============================================================ */
  const zoneE2 = {
    style: {
      ...baseRightZoneStyle,
      flex: "0 0 480px",
      minWidth: 480,
      maxWidth: 480,
    },
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "",
        title: "",
        content: (
          <PrintMenuStandard
            items={[
              ...buildingSystems,
              ...technicalSystems,
              ...specificAssets,
            ]}
            getItemId={(item) => item.id}
            getItemLabel={(item) =>
              `${item.title || item.label || item.id} ${
                item.family ? `· ${item.family}` : ""
              }`
            }
            isActive={(item) => item.active !== false}
            templates={[
              { value: "scope-summary", label: "Scope Summary" },
              { value: "scope-detailed", label: "Detailed Scope Report" },
              { value: "scope-review", label: "Review Package" },
            ]}
            defaultTemplate="scope-summary"
            renderTemplatePreview={(templateValue) => {
              if (!templateValue) return "No template";
              switch (templateValue) {
                case "scope-summary":
                  return "Summary layout";
                case "scope-detailed":
                  return "Detailed layout";
                case "scope-review":
                  return "Review package";
                default:
                  return "Template preview";
              }
            }}
            onGenerate={(options) => {
              console.log("Generate scope report:", options);
            }}
          />
        ),
      },
    ],
  };

  /* ============================================================
     ZONE F1
  ============================================================ */
  const zoneF1 = {
    style: baseLeftZoneStyle,
    gap: 16,
    sectionGap: 6,
    sections: [
      {
        title: "",
        content: <div>Aucun élément sélectionné.</div>,
      },
    ],
  };

  /* ============================================================
     ZONE F2
  ============================================================ */
  const zoneF2 = {
    style: baseRightZoneStyle,
    gap: 12,
    sectionGap: 6,
    sections: [
      {
        icon: "/assets/icones/container/shield.svg",
        title: "Zone F2 — Paramètres",
        content: <div>Configuration non définie…</div>,
      },
    ],
  };

  /* ============================================================
     TABS
  ============================================================ */
  const TabA = (
    <div style={tabStyle}>
      <ContainerBase {...zoneA1} />
      <ContainerBase {...zoneA2} />
    </div>
  );

  const TabB = (
    <div style={tabStyle}>
      <ContainerBase {...zoneB1} />
      <ContainerBase {...zoneB2} />
    </div>
  );

  const TabC = (
    <div style={tabStyle}>
      <ContainerBase {...zoneC1} />
      <ContainerBase {...zoneC2} />
    </div>
  );

  const TabD = (
    <div style={tabStyle}>
      <ContainerBase {...zoneD1} />
      <ContainerBase {...zoneD2} />
    </div>
  );

  const TabE = (
    <div style={tabStyle}>
      <ContainerBase {...zoneE1} />
      <ContainerBase {...zoneE2} />
    </div>
  );

  const TabF = (
    <div style={tabStyle}>
      <ContainerBase {...zoneF1} />
      {/* <ContainerBase {...zoneF2} /> */}
    </div>
  );

  const tabs = [
    { label: "Spatial Layout", content: TabA },
    { label: "Building Systems", content: TabB },
    { label: "Technical Systems", content: TabC },
    { label: "Specific Assets", content: TabD },
    { label: "Scope Summary", content: TabE },
    // { label: "Submission", content: TabF },
  ];
  const stepTexts = [
    "Definir la géometrie spatiale",
    "Définir les systèmes constructifs",
    "Définir les systèmes techniques",
    "Définir les équipements spécifiques",
    "Générer le rapport de périmètre",
  ];

  return (
    <div
      style={{
        padding: 0,
        margin: 0,
        width: "100%",
        height: "100%",
        overflowX: "hidden",
        overflowY: "visible",
      }}
    >
      {/* Toolbar volontairement conservée commentée */}
      {/* <ToolbarHeadSubpage
        style={{
          padding: 24,
          height: 50,
          minHeight: 50,
          overflow: "visible",
          background: "transparent",
        }}
        left={[
          <Button
            key="btn-new"
            variant="blue"
            onClick={() => alert("Template — action fictive")}
          >
            Nouvel élément
          </Button>,
        ]}
        center={[
          <DropdownMulti
            key="ddm-categorie"
            label="Catégorie"
            options={demoOptions}
            selectedValues={selectedCategories}
            onChange={setSelectedCategories}
            width={300}
            height={32}
          />,
          <TextFilter
            key="tf-search"
            label="Recherche"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Tapez un texte..."
            style={{ marginBottom: 0 }}
          />,
        ]}
        right={[
          <Switch
            key="sw-actifs"
            label="Actifs"
            enabled={switchOn}
            onChange={setSwitchOn}
            showText={false}
            size="small"
          />,
        ]}
      /> */}

      <JourneyTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        componentWidth="50%"
        stepsWidth="100%"
        componentOffsetLeft={0}
        headerPaddingLeft={24}
        tabsDisabled={false}
        containerStyle={{
          height: "calc(100vh - 160px)",
          minHeight: "calc(100vh - 160px)",
          overflow: "hidden",
        }}
      />
    </div>
  );
};

const tabStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 10,
  padding: 10,
  overflowY: "clip",
  overflowX: "hidden",
  minWidth: "fit-content",
  boxSizing: "border-box",
  
};

export default Subpage_102_architecture;